<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { MessageResultDTO } from '@nora-monorepo/nora-api-client';
import {
  AvatarImage,
  borderSeparatorTransparency,
  getInitials,
  HoverableUserName,
  textBlue,
  textPrimary,
  textSecondary,
  useDeviceStore,
} from '@nora-monorepo/ui-shared';

import { useAppStore } from '#nora-chat-ui/stores/app-store';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { highlight as vHighlight } from '#nora-chat-ui/utils/highlight';

interface Props {
  message: MessageResultDTO;
  highlightText: string;
}

const props = defineProps<Props>();
const emit = defineEmits(['messageClick']);

const { d } = useI18n();
const conversationStore = useConversationStore();
const conversationState = conversationStore.getConversation(
  props.message.conversationId,
);

const deviceStore = useDeviceStore();
const appStore = useAppStore();

function handleMessageClick() {
  emit('messageClick');

  if (conversationState) {
    conversationState.highlightedMessage = props.message;
    conversationStore.setCurrentConversation(conversationState.conversationListResult.id);
    if (deviceStore.isMobile) {
      appStore.toggleSidebarMobile(false);
    }
  }
}

const initials = computed(() => {
  const user = props.message.sentBy;

  if (!user) {
    return 'N/A';
  }

  return getInitials(user);
});
</script>

<template>
  <li
    class="flex pr-2 w-full cursor-pointer"
    @click="handleMessageClick"
  >
    <div class="px-2 py-3">
      <AvatarImage
        :id="message.conversationId"
        :text="initials"
      />
    </div>
    <div
      class="w-full overflow-hidden py-2 border-b"
      :class="borderSeparatorTransparency"
    >
      <div class="flex justify-between">
        <HoverableUserName
          class="font-semibold text-ellipsis overflow-hidden whitespace-nowrap mr-1"
          :class="textPrimary"
          :user="message.sentBy"
        />
        <span
          class="whitespace-nowrap text-sm"
          :class="[textSecondary]"
        >
          {{ d(message.createdAt, 'long') }}
        </span>
      </div>
      <div
        class="flex justify-between"
        :class="textSecondary"
      >
        <div class="whitespace-nowrap text-ellipsis overflow-hidden">
          <span
            v-highlight="{
              text: highlightText ?? '',
              class: `${textBlue} font-semibold`,
            }"
            class="text-ellipsis overflow-hidden text-sm text-sm"
            :class="[textSecondary]"
          >
            {{ message.message }}
          </span>
        </div>
      </div>
    </div>
  </li>
</template>
